import { makeAutoObservable, observable, runInAction } from "mobx";
import { track, type AnalyticsSource } from "@/lib/analytics";
import type { RootStore } from "./root.store";
import type { TWorkspaceCategoryField, TWorkspaceCategoryValue } from "@/types/standard-fields";

export class WorkspaceCategoriesStore {
  fieldsBySlug = observable.map<string, TWorkspaceCategoryField[]>();
  valuesByCategoryId = observable.map<string, TWorkspaceCategoryValue[]>();
  /**
   * Category ids whose values a LOAD has filled, as opposed to a mutation. The
   * two are not the same thing and the cache below must only serve the first:
   * every mutation path writes `[...this.valuesFor(id), created]`, and
   * `valuesFor` answers `[]` for a bank nobody loaded, so one `addValue` after a
   * failed load leaves a one-item list in `valuesByCategoryId` that is not the
   * bank. Serving that as complete would make a failed fetch permanent for the
   * session (NG-1566).
   */
  loadedValueCategoryIds = observable.set<string>();
  isLoading = false;
  isMutating = false;
  loadError: Error | null = null;

  private rootStore: RootStore;
  private inflightFields = new Map<string, Promise<TWorkspaceCategoryField[]>>();
  private inflightValues = new Map<string, Promise<TWorkspaceCategoryValue[]>>();

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(
      this,
      { rootStore: false, inflightFields: false, inflightValues: false } as never,
      { autoBind: true },
    );
  }

  fieldsFor(slug: string): TWorkspaceCategoryField[] {
    return this.fieldsBySlug.get(slug) ?? [];
  }

  fieldById(slug: string, categoryId: string): TWorkspaceCategoryField | undefined {
    return this.fieldsFor(slug).find((f) => f.id === categoryId);
  }

  valuesFor(categoryId: string): TWorkspaceCategoryValue[] {
    return this.valuesByCategoryId.get(categoryId) ?? [];
  }

  /**
   * Resolve a category-value id to its label, searching the loaded values of
   * every field for the slug. Returns null when the value isn't cached yet
   * (its category's values haven't been loaded) or the id is null. Used to keep
   * a denormalized ``*_label`` in sync after an id-only edit, without a reload.
   */
  labelForValue(slug: string, valueId: string | null): string | null {
    if (!valueId) return null;
    for (const field of this.fieldsFor(slug)) {
      const match = this.valuesFor(field.id).find((v) => v.id === valueId);
      if (match) return match.label;
    }
    return null;
  }

  activeCount(categoryId: string): number {
    return this.valuesFor(categoryId).filter((v) => v.is_active).length;
  }

  onlyOneActive(categoryId: string): boolean {
    return this.activeCount(categoryId) === 1;
  }

  async ensureFields(slug: string): Promise<TWorkspaceCategoryField[]> {
    const cached = this.fieldsBySlug.get(slug);
    if (cached) return cached;
    const existing = this.inflightFields.get(slug);
    if (existing) return existing;
    const promise = (async () => {
      runInAction(() => {
        this.isLoading = true;
        this.loadError = null;
      });
      try {
        const fields = await this.rootStore.workspaceCategoriesService.listFields(slug);
        runInAction(() => this.fieldsBySlug.set(slug, fields));
        return fields;
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        runInAction(() => {
          this.loadError = error;
        });
        console.error("[workspace-categories] failed to load fields", { slug, error });
        throw error;
      } finally {
        runInAction(() => {
          this.isLoading = false;
        });
      }
    })().finally(() => this.inflightFields.delete(slug));
    this.inflightFields.set(slug, promise);
    return promise;
  }

  async loadValues(slug: string, categoryId: string): Promise<TWorkspaceCategoryValue[]> {
    const key = `${slug}:${categoryId}`;
    // Already loaded → serve the cache, the way `ensureFields` does. Every
    // mutation path below writes its new list straight into
    // `valuesByCategoryId`, so a loaded bank cannot go stale under this store;
    // only a change made in another tab would, and that is what a reload is for.
    // Without this the values refetched on every mount of every page that reads
    // them, which made this the busiest reference call on the Projects page
    // (NG-1566: 624 calls over 7 days in prod, more than the projects list
    // itself).
    //
    // Keyed on `loadedValueCategoryIds`, never on the map being non-empty: a
    // mutation writes that map too, so the presence of entries says nothing
    // about whether the bank was ever fetched.
    if (this.loadedValueCategoryIds.has(categoryId)) return this.valuesFor(categoryId);
    const existing = this.inflightValues.get(key);
    if (existing) return existing;
    const promise = (async () => {
      runInAction(() => {
        this.isLoading = true;
        this.loadError = null;
      });
      try {
        const values = await this.rootStore.workspaceCategoriesService.listValues(slug, categoryId);
        runInAction(() => {
          this.valuesByCategoryId.set(categoryId, values);
          this.loadedValueCategoryIds.add(categoryId);
        });
        return values;
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        runInAction(() => {
          this.loadError = error;
        });
        console.error("[workspace-categories] failed to load values", { slug, categoryId, error });
        throw error;
      } finally {
        runInAction(() => {
          this.isLoading = false;
        });
      }
    })().finally(() => this.inflightValues.delete(key));
    this.inflightValues.set(key, promise);
    return promise;
  }

  private bumpActiveCount(slug: string, categoryId: string, delta: number): void {
    const fields = this.fieldsBySlug.get(slug);
    if (!fields) return;
    this.fieldsBySlug.set(
      slug,
      fields.map((f) =>
        f.id === categoryId ? { ...f, active_value_count: f.active_value_count + delta } : f,
      ),
    );
  }

  async addValue(
    slug: string,
    categoryId: string,
    label: string,
    source: AnalyticsSource = "page",
  ): Promise<TWorkspaceCategoryValue> {
    runInAction(() => {
      this.isMutating = true;
    });
    try {
      const created = await this.rootStore.workspaceCategoriesService.createValue(
        slug,
        categoryId,
        {
          label,
        },
      );
      runInAction(() => {
        this.valuesByCategoryId.set(categoryId, [...this.valuesFor(categoryId), created]);
        this.bumpActiveCount(slug, categoryId, created.is_active ? 1 : 0);
      });
      track("category_value_added", { category_id: categoryId, value_id: created.id, source });
      return created;
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  async renameValue(
    slug: string,
    categoryId: string,
    valueId: string,
    label: string,
    source: AnalyticsSource = "page",
  ): Promise<TWorkspaceCategoryValue> {
    runInAction(() => {
      this.isMutating = true;
    });
    try {
      const updated = await this.rootStore.workspaceCategoriesService.updateValue(
        slug,
        categoryId,
        valueId,
        { label },
      );
      runInAction(() => {
        this.valuesByCategoryId.set(
          categoryId,
          this.valuesFor(categoryId).map((v) => (v.id === valueId ? updated : v)),
        );
      });
      track("category_value_updated", {
        category_id: categoryId,
        value_id: valueId,
        fields: ["label"],
        source,
      });
      return updated;
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  /**
   * Hard-delete a value and drop it from the value cache on success. Only ever
   * called for an inactive value (the UI offers Delete only on inactive,
   * non-default rows and the BE 409s otherwise), so `active_value_count` is
   * unaffected. On a delete-block 409 the service rejects and the value stays in
   * the cache; the caller surfaces the block reason.
   */
  async deleteValue(
    slug: string,
    categoryId: string,
    valueId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    runInAction(() => {
      this.isMutating = true;
    });
    try {
      await this.rootStore.workspaceCategoriesService.deleteValue(slug, categoryId, valueId);
      runInAction(() => {
        this.valuesByCategoryId.set(
          categoryId,
          this.valuesFor(categoryId).filter((v) => v.id !== valueId),
        );
      });
      track("category_value_deleted", { category_id: categoryId, value_id: valueId, source });
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  async setValueActive(
    slug: string,
    categoryId: string,
    valueId: string,
    isActive: boolean,
    source: AnalyticsSource = "page",
  ): Promise<TWorkspaceCategoryValue> {
    runInAction(() => {
      this.isMutating = true;
    });
    try {
      const before = this.valuesFor(categoryId).find((v) => v.id === valueId);
      const updated = await this.rootStore.workspaceCategoriesService.updateValue(
        slug,
        categoryId,
        valueId,
        { is_active: isActive },
      );
      runInAction(() => {
        this.valuesByCategoryId.set(
          categoryId,
          this.valuesFor(categoryId).map((v) => (v.id === valueId ? updated : v)),
        );
        if (before && before.is_active !== updated.is_active) {
          this.bumpActiveCount(slug, categoryId, updated.is_active ? 1 : -1);
        }
      });
      track(isActive ? "category_value_activated" : "category_value_deactivated", {
        category_id: categoryId,
        value_id: valueId,
        source,
      });
      return updated;
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  /**
   * Move the value at array index `from` to index `to`, persisting the new
   * order. Optimistic: the splice shows immediately; on failure the cache is
   * reverted and the error logged + rethrown for the caller to toast.
   */
  async reorderValues(
    slug: string,
    categoryId: string,
    from: number,
    to: number,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const current = this.valuesFor(categoryId);
    if (from === to || from < 0 || to < 0 || from >= current.length || to >= current.length) {
      return;
    }
    const next = [...current];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    runInAction(() => {
      this.valuesByCategoryId.set(categoryId, next);
      this.isMutating = true;
    });
    try {
      const persisted = await this.rootStore.workspaceCategoriesService.reorderValues(
        slug,
        categoryId,
        next.map((v) => v.id),
      );
      runInAction(() => this.valuesByCategoryId.set(categoryId, persisted));
      track("category_values_reordered", { category_id: categoryId, source });
    } catch (error) {
      runInAction(() => this.valuesByCategoryId.set(categoryId, current));
      console.error("[workspace-categories] reorder failed", {
        slug,
        categoryId,
        from,
        to,
        op: "reorder",
        error,
      });
      throw error;
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }
}
